import React, { useState, useEffect } from 'react';
import { Box, IconButton, Stack, Typography, useTheme, useMediaQuery } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TelegramIcon from '@mui/icons-material/Telegram';
import PhoneIcon from '@mui/icons-material/Phone';
import axios from 'axios';

/**
 * Component for displaying contact buttons (WhatsApp, Telegram, Phone) as icon buttons
 */
const ContactButtons = ({ spacing = 2 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await axios.get('/api/settings');
        if (response.data && response.data.contactPhone) {
          // Remove all non-digit characters for links
          const cleanPhone = response.data.contactPhone.replace(/\D/g, '');
          setPhone(cleanPhone);
        }
      } catch (error) {
        console.error('Error fetching contact info:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContactInfo();
  }, []);

  // Don't render anything while loading
  if (loading || !phone) return null;

  // Размеры кнопок в зависимости от устройства (увеличены на 30%)
  const buttonSize = isMobile ? 56 : 52; // Немного уменьшаем размер кнопок на мобильных устройствах
  const iconSize = isMobile ? "medium" : "small";

  return (
    <Box 
      sx={{ 
        mt: 0.5,
        py: { xs: 1, md: 1 }, // Уменьшаем вертикальный отступ на мобильных
        px: { xs: 1, md: 0 },
        borderRadius: 2,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'transparent', // Убираем фон, так как фон уже добавлен в родительском компоненте
      }}
    >
      {/* Удалили дублирующийся заголовок "Связаться с нами", так как он уже есть в родительском компоненте */}
      <Stack 
        direction="row" 
        spacing={spacing}
        sx={{ 
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          flexWrap: { xs: 'nowrap', sm: 'nowrap' }, // Предотвращаем перенос кнопок на новую строку
        }}
      >
        <IconButton
          color="success"
          href={`https://wa.me/${phone}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            backgroundColor: 'rgba(37, 211, 102, 0.95)', // Увеличиваем непрозрачность
            color: '#fff',
            '&:hover': {
              backgroundColor: 'rgba(37, 211, 102, 1)',
            },
            width: buttonSize,
            height: buttonSize,
            boxShadow: '0 3px 8px rgba(0,0,0,0.4)', // Увеличиваем тень
            border: '2px solid rgba(255, 255, 255, 0.3)' // Добавляем бордер для лучшей видимости
          }}
          aria-label="WhatsApp"
        >
          <WhatsAppIcon fontSize={iconSize} />
        </IconButton>
        <IconButton
          color="primary"
          href={`https://telegram.me/+${phone}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            backgroundColor: 'rgba(0, 136, 204, 0.95)', // Увеличиваем непрозрачность
            color: '#fff',
            '&:hover': {
              backgroundColor: 'rgba(0, 136, 204, 1)',
            },
            width: buttonSize,
            height: buttonSize,
            boxShadow: '0 3px 8px rgba(0,0,0,0.4)', // Увеличиваем тень
            border: '2px solid rgba(255, 255, 255, 0.3)' // Добавляем бордер для лучшей видимости
          }}
          aria-label="Telegram"
        >
          <TelegramIcon fontSize={iconSize} />
        </IconButton>
        <IconButton
          color="secondary"
          href={`tel:+${phone}`}
          sx={{
            backgroundColor: 'rgba(156, 39, 176, 0.95)', // Увеличиваем непрозрачность
            color: '#fff',
            '&:hover': {
              backgroundColor: 'rgba(156, 39, 176, 1)',
            },
            width: buttonSize,
            height: buttonSize,
            boxShadow: '0 3px 8px rgba(0,0,0,0.4)', // Увеличиваем тень
            border: '2px solid rgba(255, 255, 255, 0.3)' // Добавляем бордер для лучшей видимости
          }}
          aria-label="Позвонить"
        >
          <PhoneIcon fontSize={iconSize} />
        </IconButton>
      </Stack>
    </Box>
  );
};

export default ContactButtons;
